import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Progress } from '../components/ui/progress';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { knowledgeBaseAPI, apiKeyAPI, vendorAPI } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, FileText, Loader2, Database, AlertCircle, CheckCircle, Clock, FileType, File } from 'lucide-react';

export default function KnowledgeBasePage() {
    const [knowledgeBases, setKnowledgeBases] = useState([]);
    const [selectedKB, setSelectedKB] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Dialog states
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const [kbName, setKbName] = useState('');
    const [kbDescription, setKbDescription] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);

    // Textbook Mode State
    const [uploadMode, setUploadMode] = useState('general'); // 'general' | 'textbook'
    const [apiKeys, setApiKeys] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [selectedApiKey, setSelectedApiKey] = useState('');
    const [selectedModel, setSelectedModel] = useState('');

    useEffect(() => {
        loadKnowledgeBases();
        loadAuxData();
    }, []);

    useEffect(() => {
        if (selectedKB) {
            loadDocuments(selectedKB.id);
        }
    }, [selectedKB]);

    const loadAuxData = async () => {
        try {
            const [keysRes, vendorsRes] = await Promise.all([
                apiKeyAPI.list(),
                vendorAPI.list()
            ]);
            setApiKeys(keysRes.data);
            setVendors(vendorsRes.data);
        } catch (error) {
            console.error("Failed to load aux data", error);
        }
    };

    const loadKnowledgeBases = async () => {
        try {
            const res = await knowledgeBaseAPI.list();
            setKnowledgeBases(res.data);
        } catch (error) {
            toast.error('Failed to load knowledge bases');
        } finally {
            setLoading(false);
        }
    };

    const loadDocuments = async (kbId) => {
        try {
            const res = await knowledgeBaseAPI.listDocuments(kbId);
            setDocuments(res.data);
        } catch (error) {
            toast.error('Failed to load documents');
        }
    };

    const handleCreateKB = async () => {
        if (!kbName.trim()) {
            toast.error('Please enter a name');
            return;
        }

        try {
            const res = await knowledgeBaseAPI.create({
                name: kbName,
                description: kbDescription
            });
            setKnowledgeBases([...knowledgeBases, res.data]);
            setShowCreateDialog(false);
            setKbName('');
            setKbDescription('');
            toast.success('Knowledge base created');
        } catch (error) {
            toast.error('Failed to create knowledge base');
        }
    };

    const handleDeleteKB = async (kbId) => {
        if (!window.confirm('Delete this knowledge base and all its documents?')) return;

        try {
            await knowledgeBaseAPI.delete(kbId);
            setKnowledgeBases(knowledgeBases.filter(kb => kb.id !== kbId));
            if (selectedKB?.id === kbId) {
                setSelectedKB(null);
                setDocuments([]);
            }
            toast.success('Knowledge base deleted');
        } catch (error) {
            toast.error('Failed to delete knowledge base');
        }
    };

    const handleUploadDocument = async () => {
        if (!selectedFile) {
            toast.error('Please select a file');
            return;
        }

        if (uploadMode === 'textbook') {
            if (!selectedApiKey || !selectedModel) {
                toast.error('API Key and Model are required for Textbook Mode');
                return;
            }
        }

        setUploading(true);
        setUploadProgress(0);

        try {
            // Generate unique docId: timestamp + random
            const docId = `${Date.now()}-${Math.floor(10000 + Math.random() * 90000)}`;
            console.log('Generated docId:', docId);

            const options = {
                mode: uploadMode,
                apiKeyId: selectedApiKey,
                modelId: selectedModel
            };

            await knowledgeBaseAPI.uploadDocument(selectedKB.id, selectedFile, docId, options, (progress) => {
                setUploadProgress(progress);
            });

            toast.success(uploadMode === 'textbook' ? 'Document uploaded. Processing in background...' : 'Document uploaded successfully');

            setShowUploadDialog(false);
            setSelectedFile(null);
            setUploadProgress(0);

            // Refresh KB list/docs
            loadDocuments(selectedKB.id);
            loadKnowledgeBases();

        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload document');
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleDeleteDocument = async (docId) => {
        if (!window.confirm('Delete this document?')) return;

        try {
            await knowledgeBaseAPI.deleteDocument(selectedKB.id, docId);
            setDocuments(documents.filter(doc => doc.id !== docId));
            toast.success('Document deleted');

            // Refresh KB list to update document count
            loadKnowledgeBases();
        } catch (error) {
            toast.error('Failed to delete document');
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <CheckCircle className="w-4 h-4 text-green-600" />;
            case 'processing':
                return <Clock className="w-4 h-4 text-yellow-600 animate-pulse" />;
            case 'failed':
                return <AlertCircle className="w-4 h-4 text-red-600" />;
            default:
                return null;
        }
    };

    const getFileIcon = (filename) => {
        const ext = filename.toLowerCase().split('.').pop();
        switch (ext) {
            case 'pdf':
                return <FileType className="w-4 h-4 text-red-600" />;
            case 'docx':
            case 'doc':
                return <FileType className="w-4 h-4 text-blue-600" />;
            case 'md':
            case 'txt':
                return <FileText className="w-4 h-4 text-muted-foreground" />;
            default:
                return <File className="w-4 h-4 text-muted-foreground" />;
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight font-heading">Knowledge Bases</h1>
                        <p className="text-sm text-muted-foreground">
                            Manage your knowledge bases for RAG-enhanced conversations
                        </p>
                    </div>
                    <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                        <DialogTrigger asChild>
                            <Button>
                                <Plus className="w-4 h-4 mr-2" />
                                New Knowledge Base
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Create Knowledge Base</DialogTitle>
                                <DialogDescription>
                                    Create a new knowledge base to store documents
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label>Name</Label>
                                    <Input
                                        placeholder="My Knowledge Base"
                                        value={kbName}
                                        onChange={(e) => setKbName(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Description (Optional)</Label>
                                    <Textarea
                                        placeholder="Description..."
                                        value={kbDescription}
                                        onChange={(e) => setKbDescription(e.target.value)}
                                        rows={3}
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                                <Button onClick={handleCreateKB}>Create</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                    {/* Knowledge Bases List */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Your Knowledge Bases</CardTitle>
                            <CardDescription>
                                {knowledgeBases.length} knowledge base{knowledgeBases.length !== 1 ? 's' : ''}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {knowledgeBases.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>No knowledge bases yet</p>
                                    <p className="text-sm">Create one to get started</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {knowledgeBases.map((kb) => (
                                        <div
                                            key={kb.id}
                                            className={`p-4 rounded-lg border cursor-pointer transition-colors hover:border-zinc-400 ${selectedKB?.id === kb.id ? 'border-zinc-900 bg-zinc-50' : ''
                                                }`}
                                            onClick={() => setSelectedKB(kb)}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium truncate">{kb.name}</p>
                                                    {kb.description && (
                                                        <p className="text-sm text-muted-foreground truncate">{kb.description}</p>
                                                    )}
                                                    <div className="flex items-center gap-2 mt-2">
                                                        <Badge variant="outline" className="text-xs">
                                                            {kb.document_count} document{kb.document_count !== 1 ? 's' : ''}
                                                        </Badge>
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={(e) => { e.stopPropagation(); handleDeleteKB(kb.id); }}
                                                >
                                                    <Trash2 className="w-4 h-4 text-destructive" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Documents List */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Documents</CardTitle>
                                    <CardDescription>
                                        {selectedKB ? selectedKB.name : 'Select a knowledge base'}
                                    </CardDescription>
                                </div>
                                {selectedKB && (
                                    <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
                                        <DialogTrigger asChild>
                                            <Button size="sm">
                                                <Upload className="w-4 h-4 mr-2" />
                                                Upload
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Upload Document</DialogTitle>
                                                <DialogDescription>
                                                    Upload a text document or PDF to {selectedKB.name}
                                                </DialogDescription>
                                            </DialogHeader>
                                            <div className="space-y-4 py-4">
                                                {/* Mode Toggle */}
                                                <div className="flex flex-col gap-2 p-3 bg-muted/50 rounded-lg">
                                                    <Label>Processing Mode</Label>
                                                    <div className="flex items-center gap-4">
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="radio"
                                                                id="mode-general"
                                                                name="mode"
                                                                value="general"
                                                                checked={uploadMode === 'general'}
                                                                onChange={(e) => setUploadMode(e.target.value)}
                                                            />
                                                            <Label htmlFor="mode-general" className="font-normal cursor-pointer">General</Label>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="radio"
                                                                id="mode-textbook"
                                                                name="mode"
                                                                value="textbook"
                                                                checked={uploadMode === 'textbook'}
                                                                onChange={(e) => setUploadMode(e.target.value)}
                                                            />
                                                            <Label htmlFor="mode-textbook" className="font-normal cursor-pointer">Textbook/Academic</Label>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Textbook Mode Config */}
                                                {uploadMode === 'textbook' && (
                                                    <div className="space-y-3 p-3 border rounded-lg animate-in slide-in-from-top-2">
                                                        <div className="space-y-2">
                                                            <Label>Provider (API Key)</Label>
                                                            <select
                                                                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                                value={selectedApiKey}
                                                                onChange={(e) => {
                                                                    setSelectedApiKey(e.target.value);
                                                                    setSelectedModel(''); // Reset model
                                                                }}
                                                            >
                                                                <option value="">Select API Key</option>
                                                                {apiKeys.map(key => (
                                                                    <option key={key.id} value={key.id}>{key.name} ({key.vendor_code})</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <Label>Model</Label>
                                                            <select
                                                                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                                value={selectedModel}
                                                                onChange={(e) => setSelectedModel(e.target.value)}
                                                                disabled={!selectedApiKey}
                                                            >
                                                                <option value="">Select Model</option>
                                                                {(() => {
                                                                    const key = apiKeys.find(k => k.id === selectedApiKey);
                                                                    if (!key) return null;
                                                                    const vendor = vendors.find(v => v.vendor_code === key.vendor_code);
                                                                    return vendor?.supported_models?.map(m => (
                                                                        <option key={m} value={m}>{m}</option>
                                                                    ));
                                                                })()}
                                                            </select>
                                                        </div>
                                                        <p className="text-xs text-muted-foreground">
                                                            Uses VLM to semantically restore tables and layouts.
                                                            <br />Note: High token consumption.
                                                        </p>
                                                    </div>
                                                )}

                                                <div className="space-y-2">
                                                    <Label>File</Label>
                                                    <Input
                                                        type="file"
                                                        accept=".txt,.md,.text,.pdf,.docx,.doc"
                                                        onChange={(e) => {
                                                            const file = e.target.files[0];
                                                            if (file) {
                                                                // Validate file size (50MB)
                                                                if (file.size > 50 * 1024 * 1024) {
                                                                    toast.error('File too large. Maximum size is 50MB');
                                                                    e.target.value = '';
                                                                    return;
                                                                }
                                                                setSelectedFile(file);
                                                            }
                                                        }}
                                                    />
                                                    <p className="text-xs text-muted-foreground">
                                                        Supported formats: PDF, DOCX, TXT, MD (Max 50MB)
                                                        <br />
                                                        <span className="text-blue-600">✓ OCR enabled for scanned PDFs</span>
                                                    </p>
                                                </div>
                                            </div>
                                            <DialogFooter>
                                                <div className="flex flex-col w-full gap-4">
                                                    {uploading && (
                                                        <div className="space-y-1">
                                                            <div className="flex justify-between text-xs text-muted-foreground">
                                                                <span>{uploadProgress === 100 ? 'Processing...' : 'Uploading...'}</span>
                                                                <span>{uploadProgress}%</span>
                                                            </div>
                                                            <Progress value={uploadProgress} className="h-2" />
                                                            {uploadProgress === 100 && (
                                                                <p className="text-xs text-yellow-600 animate-pulse">
                                                                    {uploadMode === 'textbook' ? 'Starting background processing...' : 'Processing OCR...'}
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="flex justify-end gap-2">
                                                        <Button variant="outline" onClick={() => setShowUploadDialog(false)} disabled={uploading}>Cancel</Button>
                                                        <Button onClick={handleUploadDocument} disabled={uploading}>
                                                            {uploading ? (
                                                                <>
                                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                                    {uploadProgress === 100 ? 'Processing' : 'Uploading'}
                                                                </>
                                                            ) : (
                                                                'Upload'
                                                            )}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            {!selectedKB ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>Select a knowledge base to view documents</p>
                                </div>
                            ) : documents.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>No documents yet</p>
                                    <p className="text-sm">Upload a document to get started</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {documents.map((doc) => (
                                        <div
                                            key={doc.id}
                                            className="p-4 rounded-lg border"
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        {getFileIcon(doc.name)}
                                                        <p className="font-medium truncate">{doc.name}</p>
                                                        {getStatusIcon(doc.status)}
                                                    </div>
                                                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                                        <span>{(doc.file_size / 1024).toFixed(1)} KB</span>
                                                        {doc.chunk_count > 0 && (
                                                            <span>{doc.chunk_count} chunks</span>
                                                        )}
                                                        <Badge variant={doc.status === 'completed' ? 'default' : doc.status === 'failed' ? 'destructive' : 'secondary'} className="text-xs capitalize">
                                                            {doc.status}
                                                        </Badge>
                                                    </div>
                                                    {doc.error_message && (
                                                        <p className="text-xs text-red-600 mt-1">{doc.error_message}</p>
                                                    )}
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    onClick={() => handleDeleteDocument(doc.id)}
                                                >
                                                    <Trash2 className="w-4 h-4 text-destructive" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </DashboardLayout>
    );
}
